import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Calendar as CalendarIcon,
    ChevronLeft,
    ChevronRight,
    Plus,
    MapPin,
    Clock,
    Users,
    Star,
    Grid,
    List,
    Music,
    Video,
    Mic,
    Radio
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import UpcomingEvents from '../components/UpcomingEvents';
import RecentShows from '../components/RecentShows';
import TopDeepWaveTracks from '../components/TopDeepWaveTracks';

const RANGE_OPTIONS = [
    { value: 'day',     label: 'Day' },
    { value: 'week',    label: 'Week' },
    { value: 'month',   label: 'Month' },
    { value: '3months', label: '3 Months' },
    { value: 'year',    label: 'Year' },
];

const MONTH_NAMES = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
];
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const Calendar = () => {
    const { isAuthenticated, user } = useAuth();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('list');
    const [rangeMode, setRangeMode] = useState('year');
    const [selectedDate, setSelectedDate] = useState(null);
    const [filterType, setFilterType] = useState('all');

    // Calculate start/end dates for the selected range
    const getRangeBoundaries = (date, range) => {
        const start = new Date(date);
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);

        switch (range) {
            case 'day':
                end.setHours(23, 59, 59, 999);
                break;
            case 'week': {
                const dow = start.getDay();
                start.setDate(start.getDate() - dow);
                end.setDate(start.getDate() + 6);
                end.setHours(23, 59, 59, 999);
                break;
            }
            case 'month':
                start.setDate(1);
                end.setFullYear(start.getFullYear(), start.getMonth() + 1, 0);
                end.setHours(23, 59, 59, 999);
                break;
            case '3months':
                end.setMonth(start.getMonth() + 3);
                end.setHours(23, 59, 59, 999);
                break;
            case 'year':
                // Show 2 years ahead so events "more than a year away" still appear
                end.setFullYear(start.getFullYear() + 2);
                end.setHours(23, 59, 59, 999);
                break;
            default:
                end.setMonth(start.getMonth() + 1);
        }

        return { startDate: start, endDate: end };
    };

    const fetchEvents = async (date, range) => {
        try {
            setLoading(true);
            setError(null);

            const { startDate, endDate } = getRangeBoundaries(date, range);
            const startStr = startDate.toISOString().split('T')[0];
            const endStr = endDate.toISOString().split('T')[0];

            const response = await api.get(`/events/calendar?start_date=${startStr}&end_date=${endStr}`);

            if (response.data.success) {
                const sorted = [...response.data.data].sort(
                    (a, b) => new Date(a.start_date) - new Date(b.start_date)
                );
                setEvents(sorted);
            } else {
                setError('Failed to load events');
            }
        } catch (err) {
            console.error('Error fetching events:', err);
            setError('Unable to load events. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEvents(currentDate, rangeMode);
    }, [currentDate, rangeMode]);

    // When switching to grid, lock range to month (grid always shows one month)
    const handleViewChange = (mode) => {
        setViewMode(mode);
        if (mode === 'month') setRangeMode('month');
    };

    // Move forward/back by one range unit
    const goBack = () => {
        const d = new Date(currentDate);
        switch (rangeMode) {
            case 'day':     d.setDate(d.getDate() - 1); break;
            case 'week':    d.setDate(d.getDate() - 7); break;
            case 'month':   d.setMonth(d.getMonth() - 1); break;
            case '3months': d.setMonth(d.getMonth() - 3); break;
            case 'year':    d.setFullYear(d.getFullYear() - 1); break;
        }
        setCurrentDate(d);
    };

    const goForward = () => {
        const d = new Date(currentDate);
        switch (rangeMode) {
            case 'day':     d.setDate(d.getDate() + 1); break;
            case 'week':    d.setDate(d.getDate() + 7); break;
            case 'month':   d.setMonth(d.getMonth() + 1); break;
            case '3months': d.setMonth(d.getMonth() + 3); break;
            case 'year':    d.setFullYear(d.getFullYear() + 1); break;
        }
        setCurrentDate(d);
    };

    const goToToday = () => {
        setCurrentDate(new Date());
        setSelectedDate(null);
    };

    // Header label showing current range
    const getRangeLabel = () => {
        const { startDate, endDate } = getRangeBoundaries(currentDate, rangeMode);
        switch (rangeMode) {
            case 'day':
                return currentDate.toLocaleDateString(undefined, {
                    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
                });
            case 'week': {
                const s = startDate, e = endDate;
                if (s.getMonth() === e.getMonth()) {
                    return `${MONTH_NAMES[s.getMonth()]} ${s.getDate()}–${e.getDate()}, ${s.getFullYear()}`;
                }
                return `${MONTH_SHORT[s.getMonth()]} ${s.getDate()} – ${MONTH_SHORT[e.getMonth()]} ${e.getDate()}, ${s.getFullYear()}`;
            }
            case 'month':
                return `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
            case '3months': {
                const s = startDate, e = endDate;
                if (s.getFullYear() === e.getFullYear()) {
                    return `${MONTH_SHORT[s.getMonth()]} – ${MONTH_SHORT[e.getMonth()]} ${s.getFullYear()}`;
                }
                return `${MONTH_SHORT[s.getMonth()]} ${s.getFullYear()} – ${MONTH_SHORT[e.getMonth()]} ${e.getFullYear()}`;
            }
            case 'year':
                return `${currentDate.getFullYear()} – ${currentDate.getFullYear() + 1}`;
            default:
                return '';
        }
    };

    // For month grid view
    const getEventsForDate = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return events.filter(e => e.start_date.substring(0, 10) === `${y}-${m}-${d}`);
    };

    const generateCalendarDays = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const start = new Date(firstDay);
        start.setDate(start.getDate() - start.getDay());
        const end = new Date(lastDay);
        end.setDate(end.getDate() + (6 - end.getDay()));
        const days = [];
        const d = new Date(start);
        while (d <= end) { days.push(new Date(d)); d.setDate(d.getDate() + 1); }
        return days;
    };

    const calendarDays = viewMode === 'month' ? generateCalendarDays() : [];

    const filteredEvents = events.filter(e => filterType === 'all' || e.event_type === filterType);

    const getEventTypeIcon = (type) => {
        const map = {
            concert: <Music className="w-3 h-3" />,
            festival: <Radio className="w-3 h-3" />,
            livestream: <Video className="w-3 h-3" />,
            workshop: <Mic className="w-3 h-3" />,
            meetup: <Users className="w-3 h-3" />,
            release: <Star className="w-3 h-3" />
        };
        return map[type] || <CalendarIcon className="w-3 h-3" />;
    };

    const getEventTypeColor = (type) => {
        const map = {
            concert: 'bg-purple-500',
            festival: 'bg-pink-500',
            livestream: 'bg-blue-500',
            workshop: 'bg-green-500',
            meetup: 'bg-yellow-500',
            release: 'bg-red-500',
            other: 'bg-gray-500'
        };
        return map[type] || 'bg-gray-500';
    };

    if (loading && events.length === 0) {
        return (
            <div className="min-h-screen bg-gray-900 pt-20 pb-12">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="glass-card p-8 text-center">
                        <CalendarIcon className="w-12 h-12 text-gray-600 mx-auto mb-3 animate-pulse" />
                        <p className="text-gray-400">Loading calendar...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 pt-20 pb-12">
            <div className="max-w-7xl mx-auto px-6">

                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <CalendarIcon className="w-8 h-8 text-purple-400" />
                            <h1 className="text-4xl font-black gradient-text">Events Calendar</h1>
                        </div>
                        {isAuthenticated && user?.is_admin && (
                            <Link to="/admin/events/create" className="btn-primary flex items-center gap-2">
                                <Plus className="w-5 h-5" />
                                Create Event
                            </Link>
                        )}
                    </div>

                    {/* Upcoming events preview */}
                    <div className="mb-8 lg:w-1/2 lg:mx-auto">
                        <UpcomingEvents limit={3} showHeader={true} />
                    </div>

                    {/* Controls row */}
                    <div className="flex flex-col gap-4">

                        {/* Row 1: nav + range selector */}
                        <div className="flex flex-col sm:flex-row items-center gap-3 justify-between">
                            {/* Date navigation */}
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={goBack}
                                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                    aria-label="Previous"
                                >
                                    <ChevronLeft className="w-5 h-5 text-gray-400" />
                                </button>

                                <h2 className="text-lg sm:text-xl font-bold text-white text-center min-w-[200px] sm:min-w-[260px]">
                                    {getRangeLabel()}
                                </h2>

                                <button
                                    onClick={goForward}
                                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                    aria-label="Next"
                                >
                                    <ChevronRight className="w-5 h-5 text-gray-400" />
                                </button>

                                <button
                                    onClick={goToToday}
                                    className="btn-secondary text-sm px-3 py-1.5"
                                >
                                    Today
                                </button>
                            </div>

                            {/* Range selector */}
                            <div className="flex bg-gray-800 rounded-lg p-1 gap-0.5">
                                {RANGE_OPTIONS.map(opt => (
                                    <button
                                        key={opt.value}
                                        onClick={() => setRangeMode(opt.value)}
                                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                            rangeMode === opt.value
                                                ? 'bg-purple-600 text-white'
                                                : 'text-gray-400 hover:text-white'
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Row 2: filter + view toggle */}
                        <div className="flex flex-col sm:flex-row items-center gap-3 justify-between">
                            <select
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                                className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500 w-full sm:w-auto"
                            >
                                <option value="all" style={{ backgroundColor: '#1f2937' }}>All Events</option>
                                <option value="concert" style={{ backgroundColor: '#1f2937' }}>Concerts</option>
                                <option value="festival" style={{ backgroundColor: '#1f2937' }}>Festivals</option>
                                <option value="livestream" style={{ backgroundColor: '#1f2937' }}>Live Streams</option>
                                <option value="workshop" style={{ backgroundColor: '#1f2937' }}>Workshops</option>
                                <option value="meetup" style={{ backgroundColor: '#1f2937' }}>Meetups</option>
                                <option value="release" style={{ backgroundColor: '#1f2937' }}>Releases</option>
                            </select>

                            {/* List/Grid toggle */}
                            <div className="flex bg-gray-800 rounded-lg p-1">
                                <button
                                    onClick={() => handleViewChange('month')}
                                    className={`p-2 rounded-md transition-colors ${
                                        viewMode === 'month'
                                            ? 'bg-purple-600 text-white'
                                            : 'text-gray-400 hover:text-white'
                                    }`}
                                    title="Calendar view"
                                >
                                    <Grid className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => handleViewChange('list')}
                                    className={`p-2 rounded-md transition-colors ${
                                        viewMode === 'list'
                                            ? 'bg-purple-600 text-white'
                                            : 'text-gray-400 hover:text-white'
                                    }`}
                                    title="List view"
                                >
                                    <List className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="glass-card p-6 mb-8 border border-red-500/30">
                        <p className="text-red-400">{error}</p>
                        <button onClick={() => fetchEvents(currentDate, rangeMode)} className="btn-secondary mt-4">
                            Try Again
                        </button>
                    </div>
                )}

                {/* Calendar Grid / List View */}
                {viewMode === 'month' ? (
                    <div className="glass-card p-3 sm:p-6">
                        <div className="hidden md:grid grid-cols-7 gap-1 mb-4">
                            {DAY_NAMES.map(d => (
                                <div key={d} className="p-3 text-center text-gray-400 font-semibold">{d}</div>
                            ))}
                        </div>
                        <div className="grid grid-cols-2 gap-1 mb-4 md:hidden">
                            <div className="p-2 text-center text-gray-400 font-semibold text-sm">Date</div>
                            <div className="p-2 text-center text-gray-400 font-semibold text-sm">Events</div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-7 gap-1">
                            {calendarDays.map((date, index) => {
                                const isCurrentMonth = date.getMonth() === currentDate.getMonth();
                                const isToday = date.toDateString() === new Date().toDateString();
                                const dayEvents = getEventsForDate(date);
                                return (
                                    <div
                                        key={index}
                                        className={`min-h-[80px] md:min-h-[120px] border border-gray-700/50 rounded-lg cursor-pointer transition-all duration-300 relative overflow-hidden ${
                                            !isCurrentMonth ? 'opacity-50' : ''
                                        } ${dayEvents.length > 0
                                            ? `${getEventTypeColor(dayEvents[0].event_type)} hover:brightness-110`
                                            : 'hover:bg-white/5'
                                        } ${isToday ? 'ring-2 ring-purple-500/50' : ''}`}
                                        onClick={() => setSelectedDate(date)}
                                    >
                                        {dayEvents.length > 0 ? (
                                            <div className="h-full flex flex-col p-2">
                                                <div className="text-sm md:text-base font-bold mb-1 text-white">
                                                    <div className="md:hidden flex justify-between items-center">
                                                        <div>
                                                            <div className="text-xs opacity-80">{DAY_NAMES[date.getDay()]}</div>
                                                            <div className="text-lg font-bold">{date.getDate()}</div>
                                                        </div>
                                                        {dayEvents.length > 1 && (
                                                            <div className="text-xs bg-black/30 px-2 py-1 rounded-full">{dayEvents.length} events</div>
                                                        )}
                                                    </div>
                                                    <div className="hidden md:flex justify-between items-center">
                                                        <span className="text-xl font-bold">{date.getDate()}</span>
                                                        {dayEvents.length > 1 && (
                                                            <div className="text-xs bg-black/30 px-2 py-1 rounded-full">{dayEvents.length}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex-1 flex flex-col justify-center">
                                                    <div className="space-y-1">
                                                        <div className="flex items-center gap-2">
                                                            {getEventTypeIcon(dayEvents[0].event_type)}
                                                            <div className="flex-1 min-w-0">
                                                                <div className="font-semibold text-white text-sm md:text-base truncate">{dayEvents[0].title}</div>
                                                                <div className="text-xs text-white/80 truncate">
                                                                    {new Date(dayEvents[0].start_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                    {dayEvents[0].venue_name && <span> • {dayEvents[0].venue_name}</span>}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {dayEvents.length > 1 && (
                                                            <div className="text-xs text-white/70 md:hidden">+{dayEvents.length - 1} more</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="h-full flex flex-col justify-between p-2">
                                                <div className={`text-sm md:text-base font-medium ${isToday ? 'text-purple-300' : 'text-gray-300'}`}>
                                                    <div className="md:hidden">
                                                        <div className="text-xs text-gray-400">{DAY_NAMES[date.getDay()]}</div>
                                                        <div>{date.getDate()}</div>
                                                    </div>
                                                    <div className="hidden md:block">{date.getDate()}</div>
                                                </div>
                                                <div className="text-xs text-gray-500 text-center">No events</div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    // List View
                    <div className="space-y-4">
                        {loading ? (
                            <div className="glass-card p-8 text-center">
                                <CalendarIcon className="w-10 h-10 text-gray-600 mx-auto mb-3 animate-pulse" />
                                <p className="text-gray-400">Loading events...</p>
                            </div>
                        ) : filteredEvents.length === 0 ? (
                            <div className="glass-card p-8 text-center">
                                <CalendarIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                                <h3 className="text-xl font-semibold text-gray-300 mb-2">No events found</h3>
                                <p className="text-gray-400">
                                    {filterType === 'all'
                                        ? `No events scheduled for this ${rangeMode === 'year' ? 'period' : rangeMode}.`
                                        : `No ${filterType} events found.`}
                                </p>
                            </div>
                        ) : (
                            filteredEvents.map(event => (
                                <div key={event.id} className="glass-card p-4 sm:p-6 hover:bg-white/5 transition-colors">
                                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-3">
                                                <div className="flex items-center gap-3">
                                                    <div className={`p-2 rounded-lg ${getEventTypeColor(event.event_type)}`}>
                                                        {getEventTypeIcon(event.event_type)}
                                                    </div>
                                                    <div className="flex-1">
                                                        <h3 className="text-lg sm:text-xl font-bold text-white">{event.title}</h3>
                                                        <span className="text-sm text-purple-400 capitalize">{event.event_type}</span>
                                                    </div>
                                                </div>
                                                {!!event.is_featured && <Star className="w-5 h-5 text-yellow-400 self-start sm:self-center" />}
                                            </div>

                                            <div className="grid grid-cols-1 gap-2 mb-4 text-sm">
                                                <div className="flex items-center gap-2 text-gray-300">
                                                    <Clock className="w-4 h-4 flex-shrink-0" />
                                                    <span>
                                                        {new Date(event.start_date).toLocaleDateString(undefined, {
                                                            weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
                                                        })} • {new Date(event.start_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </span>
                                                </div>
                                                {event.venue_name && (
                                                    <div className="flex items-center gap-2 text-gray-300">
                                                        <MapPin className="w-4 h-4 flex-shrink-0" />
                                                        <span className="truncate">
                                                            {event.venue_name}{event.city && `, ${event.city}`}
                                                        </span>
                                                    </div>
                                                )}
                                                {event.attendee_count > 0 && (
                                                    <div className="flex items-center gap-2 text-gray-300">
                                                        <Users className="w-4 h-4 flex-shrink-0" />
                                                        <span>{event.attendee_count} attending</span>
                                                    </div>
                                                )}
                                            </div>

                                            {event.description && (
                                                <p className="text-gray-400 text-sm mb-4 line-clamp-2 sm:line-clamp-3">
                                                    {event.description}
                                                </p>
                                            )}
                                        </div>

                                        <div className="flex flex-row sm:flex-col gap-2 justify-center sm:justify-start">
                                            <Link
                                                to={`/events/${event.slug || event.id}`}
                                                className="btn-secondary text-sm flex-1 sm:flex-initial text-center touch-manipulation"
                                            >
                                                View Details
                                            </Link>
                                            {isAuthenticated && user?.is_admin && (
                                                <Link
                                                    to={`/admin/events/${event.id}/edit`}
                                                    className="btn-ghost text-sm flex-1 sm:flex-initial text-center touch-manipulation"
                                                >
                                                    Edit
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Top Tracks + Past Events */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
                    <div className="glass-card p-4 sm:p-8 border border-blue-500/20">
                        <TopDeepWaveTracks limit={5} showHeader={true} />
                    </div>
                    <div className="glass-card p-4 sm:p-8 border border-green-500/20">
                        <RecentShows limit={8} showHeader={true} />
                    </div>
                </div>

                {/* Selected Date Modal (month grid) */}
                {selectedDate && (
                    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
                        <div className="glass-card w-full sm:max-w-md p-4 sm:p-6 rounded-t-2xl sm:rounded-lg">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg sm:text-xl font-bold text-white">
                                    {selectedDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                                </h3>
                                <button onClick={() => setSelectedDate(null)} className="text-gray-400 hover:text-white p-2">✕</button>
                            </div>
                            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                                {getEventsForDate(selectedDate).map(event => (
                                    <div key={event.id} className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg">
                                        <div className={`p-2 rounded flex-shrink-0 ${getEventTypeColor(event.event_type)}`}>
                                            {getEventTypeIcon(event.event_type)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-semibold text-white truncate">{event.title}</h4>
                                            <p className="text-sm text-gray-400 truncate">
                                                {new Date(event.start_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                {event.venue_name && ` • ${event.venue_name}`}
                                            </p>
                                        </div>
                                        <Link
                                            to={`/events/${event.slug || event.id}`}
                                            className="btn-secondary text-sm"
                                            onClick={() => setSelectedDate(null)}
                                        >
                                            View
                                        </Link>
                                    </div>
                                ))}
                                {getEventsForDate(selectedDate).length === 0 && (
                                    <p className="text-gray-400 text-center py-8">No events scheduled for this day.</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Calendar;
